import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { CircleCheck, Ban } from "lucide-react";
import { cn } from "@/lib/cn";
import { toAccessDecision, type AccessDecision } from "@/lib/accessDecision";
import { toOutcomeTone, type OutcomeTone } from "@/lib/tone";

// The badge for a status that may be an outcome (d2d-UEM ds-uem/, ZTA cascade plan 5.2).
//
// The same markup as ds/ToneBadge's StatusBadge (rc-tonebadge, driven by
// data-tone, so ds.css owns every colour), read through toOutcomeTone: a
// workflow state takes a state hue, and an outcome that is a risk (failed,
// non-compliant, a missing patch) takes the severity ramp. StatusBadge alone
// would draw "failed" as a neutral closed state.
//
// `tone` overrides the lookup for the few words whose meaning depends on the
// page: "denied" is a risk in the audit log but an entitlement answer in the
// self-service portal; Android's "active" is a healthy device, where the
// shared vocabulary's "active" is SIEM's newly opened incident. The override
// is still a tone name, so it cannot invent a colour.
//
// `decision` (design evaluation D1): an access decision is drawn as its own
// thing. A squared outline chip, a shape no severity (dot and word) or state
// (round pill) uses, with a fixed glyph, so Deny is never read as Medium. The
// word goes through lib/accessDecision, which also makes rejected = denied.
// A value that isn't a decision falls back to the outcome badge.

const DECISION_GLYPH = { allow: CircleCheck, deny: Ban } as const;

/** The glyph a decision always carries, for a legend or a stat label. */
export function DecisionGlyph({ decision, size = 14 }: { decision: AccessDecision; size?: number }) {
  const Glyph = DECISION_GLYPH[decision];
  return <Glyph size={size} aria-hidden="true" />;
}

export interface OutcomeBadgeProps extends Omit<HTMLAttributes<HTMLSpanElement>, "children"> {
  /** The status as the API spells it: "failed", "notNow", "pending_reboot". */
  value: string | null | undefined;
  /** For a word whose meaning is page-specific. */
  tone?: OutcomeTone;
  /** `label` (default): dot and word, for table cells. `chip`: tinted pill, for headers and panels. */
  shape?: "label" | "chip";
  /** The value is an access decision (allow, deny, approved, rejected…): draw the decision chip. */
  decision?: boolean;
  /** Display text. Defaults to the value, with underscores as spaces. */
  children?: ReactNode;
}

export const OutcomeBadge = forwardRef<HTMLSpanElement, OutcomeBadgeProps>(function OutcomeBadge(
  { value, tone, shape = "label", decision, className, children, ...rest },
  ref,
) {
  const d = decision ? toAccessDecision(value) : null;
  if (d) {
    return (
      <span ref={ref} data-decision={d} className={cn("rc-decisionbadge", className)} {...rest}>
        <DecisionGlyph decision={d} size={12} />
        {children ?? (value ? value.replace(/_/g, " ") : d)}
      </span>
    );
  }
  return (
    <span
      ref={ref}
      data-tone={tone ?? toOutcomeTone(value)}
      data-shape={shape}
      className={cn("rc-tonebadge", className)}
      {...rest}
    >
      {shape === "label" ? <span className="rc-tonebadge__dot" aria-hidden="true" /> : null}
      {children ?? (value ? value.replace(/_/g, " ") : "Unknown")}
    </span>
  );
});
